
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Zoom,
  Slide,
  Paper,
  Chip,
  CircularProgress,
  Card,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Comment as CommentIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import axios from "axios";
import Lottie from "lottie-react";
import dinnerAnimation from "./Images/dinner.json";
import teatimeAnimation from "./Images/teatime.json";
import gearanimation from "./Images/gearicon.json";
import idletimeAnimation from "./Images/idletime.json";
import ViewJobPlan from "./ViewJobPlan";
import ShiftRemarksButton from "./shiftRemarksButton";
import { alpha } from "@mui/material/styles";
import useSessionLossRedirect from "./useSessionLossRedirect";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Gradient for meal buttons (kept exactly like your old version)
const gradientStyle = {
  background: "linear-gradient(90deg, #57f4ff,#ffff54)",
  backgroundSize: "200% 100%",
  animation: "gradient 4s ease infinite",
  color: "darkblue",
  borderColor: "red",
};

const PopupModal = ({
  jobCodes,
  username,
  operation_code,
  operation_description,
  start_datetime,
  employee_id,
  machine_code,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleSessionLoss = useSessionLossRedirect();

  // Dialogs
  const [isFirstModalOpen, setFirstModalOpen] = useState(false);
  const [isSecondModalOpen, setSecondModalOpen] = useState(false);
  const [isThirdModalOpen, setThirdModalOpen] = useState(false);

  // Selection / time snapshots
  const [selectedJob, setSelectedJob] = useState(null);
  const [startTime, setStartTime] = useState(null);            // actual start time (from server or detected)
  const [startPreviewTime, setStartPreviewTime] = useState(null); // captured for First modal
  const [endPreviewTime, setEndPreviewTime] = useState(null);      // captured on "End Operation"

  // Form / state
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shared snackbar state for inline error feedback. Sits above the
  // Dialogs (zIndex bumped) so the operator sees messages even when a
  // popup is open. Useful for the mount-time fetch where no popup is open.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error", // "error" | "warning" | "info" | "success"
  });

  // Inline error rendered directly inside the active popup (more
  // contextual than the snackbar — error appears next to the action that
  // produced it). Cleared when the operator opens/closes a popup or
  // retries the action.
  const [inlineError, setInlineError] = useState("");

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const CHARACTER_LIMIT = 50;

  // Helpers
  const pad = (n) => n.toString().padStart(2, "0");
  const formatDuration = (seconds) => {
    if (seconds == null || Number.isNaN(seconds)) return "--:--:--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };
  const diffSeconds = (a, b) => {
    if (!a || !b) return null;
    return Math.max(0, Math.floor((b.getTime() - a.getTime()) / 1000));
  };

  const glassBg = (t, opacity = 0.96) => alpha(t.palette.background.paper, opacity);

  const getOperationTypeInfo = (code) => {
    if (["IT-TT", "IT-LT", "IT-DT"].includes(code)) {
      return { type: "meal", color: theme.palette.warning.main }; // 🍽️
    } else if (code?.startsWith("IT-")) {
      return { type: "idle", color: theme.palette.info.main }; // ⏸️
    } else {
      return { type: "work", color: theme.palette.success.main }; // ⚙️
    }
  };

  // Detect ongoing operation on mount (no live timer)
  useEffect(() => {
    const fetchOngoingOperation = async () => {
      if (!employee_id || !machine_code || !start_datetime) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/Ongoingoperationlog`, {
          params: { employee_id, machine_code, start_datetime },
        });
        if (Array.isArray(response.data) && response.data.length > 0) {
          const ongoing = response.data[0];
          setSelectedJob({
            code: ongoing.operation_code,
            process: ongoing.operation_description,
          });
          setStartTime(new Date(ongoing.start_datetime));
          setSecondModalOpen(true);
        }
      } catch (error) {
        console.error("Error fetching ongoing operation:", error);
        if (handleSessionLoss(error)) {
          showSnackbar("Session expired. Redirecting to login...", "error");
        }
        // Other errors stay silent here — operator didn't trigger this
        // fetch directly, so no need to alarm them on a transient blip.
      }
    };
    fetchOngoingOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee_id, machine_code, start_datetime]);

  // Prioritized groups (meals first)
  const prioritizedJobCodes = jobCodes.filter((job) =>
    ["IT-TT", "IT-LT", "IT-DT"].includes(job.code)
  );
  const otherJobCodes = jobCodes.filter(
    (job) => !["IT-TT", "IT-LT", "IT-DT"].includes(job.code)
  );

  // Handlers
  const handleOpenFirstModal = (job) => {
    setSelectedJob(job);
    setStartPreviewTime(new Date()); // capture static display "now" for the first modal
    setInlineError(""); // clear any prior error from previous attempt
    setFirstModalOpen(true);
  };
  const handleCloseFirstModal = () => setFirstModalOpen(false);

  const handleProceed = async () => {
    if (isSubmitting || !selectedJob) return;
    setIsSubmitting(true);
    setInlineError("");

    try {
      const machineCodeResponse = await axios.get(
        `${API_BASE_URL}/getMachineCode?username=${username}`
      );

      let operation_type;
      if (selectedJob.code.startsWith("IT-")) {
        operation_type = ["IT-TT", "IT-LT", "IT-DT"].includes(selectedJob.code)
          ? "General-Meals"
          : "General-Idle";
      } else {
        operation_type = "General";
      }

      const payload = {
        employee_id: username,
        operation_code: selectedJob.code,
        operation_description: selectedJob.process,
        start_datetime: new Date().toISOString().slice(0, 19).replace("T", " "),
        machine_code: machineCodeResponse.data.machineCode,
        active_status: "Ongoing",
        operation_type,
      };

      const response = await axios.post(`${API_BASE_URL}/addOperationLog`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        const { start_datetime: serverStart } = response.data;
        setStartTime(new Date(serverStart));
        setFirstModalOpen(false);
        setSecondModalOpen(true);
      }
    } catch (error) {
      console.error("Error starting operation:", error);
      const sessionMsg = "Session expired. Redirecting to login...";
      const genericMsg = "Failed to start operation. Please try again.";
      if (handleSessionLoss(error)) {
        setInlineError(sessionMsg);
        showSnackbar(sessionMsg, "error");
      } else {
        setInlineError(genericMsg);
        showSnackbar(genericMsg, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenThirdModal = () => {
    setEndPreviewTime(new Date()); // capture once; used for display and API
    setInlineError(""); // clear any prior error from previous attempt
    setSecondModalOpen(false);
    setThirdModalOpen(true);
  };
  const handleCancelThirdModal = () => {
    setThirdModalOpen(false);
    setSecondModalOpen(true);
  };

  const handleCloseThirdModal = async () => {
    setInlineError("");
    try {
      const endStamp =
        (endPreviewTime || new Date()).toISOString().slice(0, 19).replace("T", " ");
      const trimmedRemarks = remarks.replace(/\s{2,}/g, " ").trim();

      const payload = {
        employee_id: username,
        start_datetime: startTime?.toISOString().slice(0, 19).replace("T", " "),
        end_datetime: endStamp,
        remarks: trimmedRemarks,
        active_status: "Finished",
      };

      const response = await axios.post(`${API_BASE_URL}/updateOperationLog`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        setSecondModalOpen(false);
        setThirdModalOpen(false);
        setRemarks("");
        setEndPreviewTime(null);
      }
    } catch (error) {
      console.error("Error ending operation:", error);
      const sessionMsg = "Session expired. Redirecting to login...";
      const genericMsg = "Failed to end operation. Please try again.";
      if (handleSessionLoss(error)) {
        setInlineError(sessionMsg);
        showSnackbar(sessionMsg, "error");
      } else {
        setInlineError(genericMsg);
        showSnackbar(genericMsg, "error");
      }
    }
  };

  return (
    <>
      {/* Job code buttons - EXACT style as your old grid */}
      <Grid
        container
        justifyContent="center"  // centers horizontally
        alignItems="center"      // centers vertically
      >
        {[...prioritizedJobCodes, ...otherJobCodes].map((job) => (
          <Grid
            item
            key={job.code}
            style={{ color: "red", margin: "5px", padding: "2px" }}
          >
            <Button
              style={{
                ...{
                  margin: "0px",
                  padding: "0px",
                  fontSize: "17px",
                  fontWeight: "bold",
                  backgroundColor: "lightcyan",
                  color: "darkblue",
                  borderRadius: "15px",
                  boxShadow: "0px 3px 5px rgba(0, 0, 200, 0.8)",
                },
                ...(job.code === "IT-TT" ||
                job.code === "IT-LT" ||
                job.code === "IT-DT"
                  ? gradientStyle
                  : {}),
              }}
              variant="outlined"
              color="primary"
              onClick={() => handleOpenFirstModal(job)}
            >
              <div style={{ margin: "0px", padding: "0px", width: "80px" }}>
                {job.code}
              </div>
            </Button>

            <Tooltip
              style={{ margin: "0px", padding: "0px" }}
              title={job.process}
            >
              <IconButton
                style={{ margin: "0px", padding: "0.5px" }}
                color="secondary"
                aria-label="help center"
              >
                <HelpCenterIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* First Modal - Start Operation (shows captured startPreviewTime only) */}
      <Dialog
        open={isFirstModalOpen}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: (t) => glassBg(t),
            backdropFilter: "blur(20px)",
            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
            boxShadow: 12,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: (t) =>
                selectedJob
                  ? `linear-gradient(90deg, ${getOperationTypeInfo(selectedJob.code).color}, ${t.palette.secondary.main})`
                  : `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              pb: 2,
              textAlign: "center",
              background: (t) => alpha(t.palette.primary.main, 0.06),
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Zoom in={isFirstModalOpen} timeout={500}>
              <Box>
                <PlayArrowIcon
                  sx={{
                    fontSize: 48,
                    color: (t) =>
                      selectedJob
                        ? getOperationTypeInfo(selectedJob.code).color
                        : t.palette.primary.main,
                    mb: 1,
                    filter: (t) =>
                      `drop-shadow(0 4px 8px ${alpha(t.palette.primary.main, 0.25)})`,
                  }}
                />
                <Typography variant="h5" fontWeight={600}>
                  Start Operation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Confirm to begin the selected operation
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          {inlineError && (
            <Alert
              severity="error"
              onClose={() => setInlineError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {inlineError}
            </Alert>
          )}
          <Card
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: (t) => alpha(t.palette.text.primary, 0.04),
              borderRadius: "16px",
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
              mb: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}
            >
              <Chip
                label={selectedJob?.code || ""}
                size="large"
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  px: 2,
                  backgroundColor: (t) =>
                    selectedJob
                      ? getOperationTypeInfo(selectedJob.code).color
                      : t.palette.primary.main,
                  color: "common.white",
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              {selectedJob?.process}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operation will be logged and tracked automatically
            </Typography>
          </Card>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              p: 2,
              backgroundColor: (t) => alpha(t.palette.success.main, 0.12),
              borderRadius: "12px",
              border: (t) => `1px solid ${alpha(t.palette.success.main, 0.25)}`,
            }}
          >
            <AccessTimeIcon sx={{ color: "success.main", fontSize: 20 }} />
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {startPreviewTime
                ? startPreviewTime.toLocaleString()
                : new Date().toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleProceed}
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <PlayArrowIcon />
              )
            }
            sx={{
              minWidth: 140,
              borderRadius: "12px",
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              color: "common.white",
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.dark} 100%)`,
              boxShadow: (t) => `0 6px 20px ${alpha(t.palette.success.main, 0.4)}`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: (t) => `0 8px 25px ${alpha(t.palette.success.main, 0.5)}`,
              },
            }}
          >
            {isSubmitting ? "Starting..." : "Start Now"}
          </Button>
          <Button
            onClick={handleCloseFirstModal}
            variant="outlined"
            size="large"
            startIcon={<CancelIcon />}
            sx={{
              minWidth: 140,
              borderRadius: "12px",
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "error.main",
              color: "error.main",
              "&:hover": {
                backgroundColor: (t) => alpha(t.palette.error.main, 0.08),
                borderColor: "error.dark",
                transform: "translateY(-2px)",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second Modal - Ongoing Operation (no live duration) */}
      <Dialog
        open={isSecondModalOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: (t) =>
              selectedJob
                ? `linear-gradient(145deg, ${alpha(
                    getOperationTypeInfo(selectedJob.code).color,
                    0.15
                  )} 0%, ${alpha(
                    getOperationTypeInfo(selectedJob.code).color,
                    0.25
                  )} 50%, ${alpha(t.palette.background.default, 0.96)} 100%)`
                : `linear-gradient(145deg, ${alpha(t.palette.primary.main, 0.2)} 0%, ${alpha(
                    t.palette.secondary.main,
                    0.3
                  )} 50%, ${alpha(t.palette.background.default, 0.96)} 100%)`,
            backdropFilter: "blur(20px)",
            border: (t) =>
              `1px solid ${
                selectedJob
                  ? alpha(getOperationTypeInfo(selectedJob.code).color, 0.35)
                  : alpha(t.palette.divider, 0.9)
              }`,
            boxShadow: (t) => `0 25px 45px ${alpha(t.palette.common.black, 0.35)}`,
            overflow: "hidden",
            position: "relative",
            minHeight: isMobile ? "400px" : "500px",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              background: (t) =>
                selectedJob
                  ? `linear-gradient(135deg, ${alpha(
                      getOperationTypeInfo(selectedJob.code).color,
                      0.3
                    )} 0%, ${alpha(
                      getOperationTypeInfo(selectedJob.code).color,
                      0.2
                    )} 50%, ${alpha(t.palette.background.default, 0.9)} 100%)`
                  : `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.3)} 0%, ${alpha(
                      t.palette.secondary.main,
                      0.3
                    )} 50%, ${alpha(t.palette.background.default, 0.9)} 100%)`,
              borderBottom: (t) =>
                `1px solid ${
                  selectedJob
                    ? alpha(getOperationTypeInfo(selectedJob.code).color, 0.45)
                    : t.palette.divider
                }`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <ShiftRemarksButton Employee_ID={username} />
                <ViewJobPlan username={username} />
              </Box>
              {/* (Removed live duration chip) */}
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: "common.white",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                  mb: 1,
                }}
              >
                Operation in Progress
              </Typography>
              <Chip
                label={selectedJob?.code || ""}
                size="large"
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  backgroundColor: (t) =>
                    selectedJob
                      ? getOperationTypeInfo(selectedJob.code).color
                      : t.palette.primary.main,
                  color: "common.white",
                }}
              />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: "relative", height: "400px" }}>
          {/* Animation Background */}
          {selectedJob && !selectedJob.code.startsWith("IT-") && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 0,
              }}
            >
              <Lottie animationData={gearanimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </Box>
          )}
          {selectedJob && (selectedJob.code === "IT-DT" || selectedJob.code === "IT-LT") && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Lottie animationData={dinnerAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </Box>
          )}
          {selectedJob && selectedJob.code === "IT-TT" && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Lottie animationData={teatimeAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </Box>
          )}
          {selectedJob &&
            selectedJob.code.startsWith("IT-") &&
            !["IT-DT", "IT-LT", "IT-TT"].includes(selectedJob.code) && (
              <Box sx={{ position: "absolute", inset: 0, zIndex: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Lottie animationData={idletimeAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
              </Box>
            )}

          {/* Overlay - start time only */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: (t) =>
                selectedJob
                  ? `linear-gradient(transparent, ${alpha(
                      getOperationTypeInfo(selectedJob.code).color,
                      0.2
                    )} 30%, ${alpha(t.palette.background.default, 0.96)} 100%)`
                  : `linear-gradient(transparent, ${alpha(
                      t.palette.primary.main,
                      0.2
                    )} 30%, ${alpha(t.palette.background.default, 0.96)} 100%)`,
              p: 3,
              textAlign: "center",
              backdropFilter: "blur(10px)",
              zIndex: 1,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: "common.white", textShadow: "0 2px 8px rgba(0,0,0,0.8)", mb: 2 }}
            >
              {selectedJob?.process}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Paper
                sx={{
                  px: 2,
                  py: 1,
                  background: (t) =>
                    selectedJob
                      ? `linear-gradient(135deg, ${alpha(
                          getOperationTypeInfo(selectedJob.code).color,
                          0.25
                        )} 0%, ${alpha(t.palette.common.white, 0.15)} 100%)`
                      : `linear-gradient(135deg, ${alpha(
                          t.palette.primary.main,
                          0.25
                        )} 0%, ${alpha(t.palette.common.white, 0.15)} 100%)`,
                  backdropFilter: "blur(15px)",
                  borderRadius: "12px",
                  border: (t) =>
                    `1px solid ${
                      selectedJob
                        ? alpha(getOperationTypeInfo(selectedJob.code).color, 0.4)
                        : alpha(t.palette.common.white, 0.25)
                    }`,
                }}
              >
                <Typography variant="body1" fontWeight={600} sx={{ color: "common.white" }}>
                  Started: {startTime ? startTime.toLocaleTimeString() : "--:--:--"}
                </Typography>
              </Paper>
            </Box>

            <Button
              onClick={handleOpenThirdModal}
              variant="contained"
              size="large"
              startIcon={<StopIcon />}
              sx={{
                minWidth: 200,
                borderRadius: "16px",
                py: 1.5,
                fontWeight: 700,
                fontSize: "1.1rem",
                textTransform: "none",
                color: "common.white",
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`,
                boxShadow: (t) => `0 8px 25px ${alpha(t.palette.error.main, 0.4)}`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: (t) => `0 12px 35px ${alpha(t.palette.error.main, 0.5)}`,
                },
              }}
            >
              End Operation
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Third Modal - End Operation (duration computed once here) */}
      <Dialog
        open={isThirdModalOpen}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: (t) => glassBg(t),
            backdropFilter: "blur(20px)",
            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
            boxShadow: 12,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: (t) =>
                `linear-gradient(90deg, ${t.palette.error.main}, ${t.palette.warning.main})`,
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              pb: 2,
              textAlign: "center",
              background: (t) => alpha(t.palette.error.main, 0.06),
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Zoom in={isThirdModalOpen} timeout={500}>
              <Box>
                <WarningIcon
                  sx={{
                    fontSize: 48,
                    color: "error.main",
                    mb: 1,
                    filter: (t) => `drop-shadow(0 4px 8px ${alpha(t.palette.error.main, 0.3)})`,
                  }}
                />
                <Typography variant="h5" fontWeight={600}>
                  End Operation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Confirm to complete and log this operation
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {inlineError && (
            <Alert
              severity="error"
              onClose={() => setInlineError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              {inlineError}
            </Alert>
          )}
          <Card
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: (t) => alpha(t.palette.text.primary, 0.04),
              borderRadius: "16px",
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
              mb: 3,
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <Chip
                label={selectedJob?.code || ""}
                size="large"
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  backgroundColor: (t) =>
                    selectedJob
                      ? getOperationTypeInfo(selectedJob.code).color
                      : t.palette.primary.main,
                  color: "common.white",
                }}
              />
            </Box>

            <Typography variant="h6" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              {selectedJob?.process}
            </Typography>

            {/* Duration computed once using captured endPreviewTime */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total Duration:{" "}
              <strong>
                {formatDuration(diffSeconds(startTime, endPreviewTime || new Date()))}
              </strong>
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: (t) => alpha(t.palette.success.main, 0.12),
                border: (t) => `1px solid ${alpha(t.palette.success.main, 0.25)}`,
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
            >
              <span>Started: {startTime ? startTime.toLocaleTimeString() : "--:--:--"}</span>
              <span>Ending: {(endPreviewTime || new Date()).toLocaleTimeString()}</span>
            </Box>
          </Card>

          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              label="Operation Remarks"
              placeholder="Add any comments about this operation..."
              multiline
              rows={3}
              value={remarks}
              onChange={(e) => {
                if (e.target.value.length <= CHARACTER_LIMIT) {
                  setRemarks(e.target.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <CommentIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: (t) => alpha(t.palette.text.primary, 0.04),
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Optional: Add notes about this operation
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    remarks.length >= CHARACTER_LIMIT ? "error.main" : "text.secondary",
                  fontWeight: remarks.length >= CHARACTER_LIMIT ? "bold" : "normal",
                }}
              >
                {remarks.length}/{CHARACTER_LIMIT}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleCloseThirdModal}
            variant="contained"
            size="large"
            startIcon={<CheckCircleIcon />}
            sx={{
              minWidth: 160,
              borderRadius: "12px",
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              color: "common.white",
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.dark} 100%)`,
              boxShadow: (t) => `0 6px 20px ${alpha(t.palette.success.main, 0.4)}`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: (t) => `0 8px 25px ${alpha(t.palette.success.main, 0.5)}`,
              },
            }}
          >
            Complete Operation
          </Button>
          <Button
            onClick={handleCancelThirdModal}
            variant="outlined"
            size="large"
            startIcon={<CancelIcon />}
            sx={{
              minWidth: 140,
              borderRadius: "12px",
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "error.main",
              color: "error.main",
              "&:hover": {
                backgroundColor: (t) => alpha(t.palette.error.main, 0.08),
                borderColor: "error.dark",
                transform: "translateY(-2px)",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline error / status feedback. Sits above the Dialogs so the
          operator can read messages even while a popup is open. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </>
  );
};

export default PopupModal;